import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetClientUsers } from 'services/clientUserManagment';
import {
  IGetClientUsersSearchParams,
  IUserResponse,
} from 'services/clientUserManagment/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getNodes } from 'utils/table/nodes.utils';

enum USERS_COLUMNS {
  nickname,
  firstName,
  lastName,
  email,
  phoneNumber,
}

const initialValues: IGetClientUsersSearchParams = {
  userName: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

const ClientUsers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const i18nextPrefix = 'user';

  const columnsVisibility = useTableColumnsVisibility(
    'ClientUsers',
    USERS_COLUMNS
  );

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(
      false,
      true,
      columnsVisibility.tableId
    );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] || initialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetClientUsers(filters);

  const tableData = getNodes<IUserResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/accounts`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`user.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={USERS_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix=''
                  customI18next='input.label'
                />
              </div>
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create', { replace: false })}>
                <Button.Text
                  text={t('button.create-what', { what: t('user.what') })}
                />
                <Button.Icon icon={<PiUserPlus />} />
              </Button>
            </>
          }>
          <QueryFilter<IGetClientUsersSearchParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IGetClientUsersSearchParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='userName'
                    label={t('input.label.nickname')}
                  />
                  <FormInput.Text
                    name='firstName'
                    label={t('input.label.firstName')}
                  />
                  <FormInput.Text
                    name='lastName'
                    label={t('input.label.lastName')}
                  />
                  <FormInput.Text
                    name='email'
                    label={t('input.label.email')}
                  />
                  <FormInput.Text
                    name='phoneNumber'
                    label={t('input.label.phoneNumber')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={select}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        displayMode
        columns={[
          {
            label: t('input.label.nickname'),
            renderCell: ({ userName }) => (
              <TableItem>
                <TableItem.Text text={userName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.nickname),
          },
          {
            label: t('input.label.firstName'),
            renderCell: ({ firstName }) => (
              <TableItem>
                <TableItem.Text text={firstName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.firstName),
          },
          {
            label: t('input.label.lastName'),
            renderCell: ({ lastName }) => (
              <TableItem>
                <TableItem.Text text={lastName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.lastName),
          },
          {
            label: t('input.label.email'),
            renderCell: ({ email }) => (
              <TableItem>
                <TableItem.Text text={email} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.email),
          },
          {
            label: t('input.label.phoneNumber'),
            renderCell: ({ phoneNumber }) => (
              <TableItem>
                <TableItem.Text text={phoneNumber} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(USERS_COLUMNS.phoneNumber),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default ClientUsers;
